// Model tabel datalog: definisi kolom + pembentukan baris. Dipakai bareng
// Report Datalog & Map Viewer. Sengaja NOL import ber-alias biar bisa diuji node.

import { toDuration, formatDateTime } from './date.js';
import { DISPLAY_DATETIME } from './apiDatetime.js';
import { formatNum } from './number.js';
import { convertDecimalToDMS } from './coordinateFormat.js';
import { kmhToKnot, metersToNm } from './units.js';

// Urutan kanonik posisi mesin. Nama di luar daftar didorong ke belakang.
export function sortPositions(arr) {
  const order = ['PORT', 'CENTER', 'STARBOARD'];
  return [...arr].sort((a, b) => {
    const ia = order.indexOf(String(a).toUpperCase());
    const ib = order.indexOf(String(b).toUpperCase());
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

// Definisi kolom tabel datalog, bergrup per kategori. rpmTypeLabel &
// flowmeterLabel disuntik lewat config karena versi aslinya import alias
// path absolut yang gak bisa di-resolve node (lihat task-1-brief).
export function buildDatalogColumns(config) {
  const { aeCount = 0, rpmTypes } = config;

  const columns = {
    dateTime: [
      {
        key: 'dateTime', label: 'Date Time', minWidth: 180, visible: true,
      },
    ],

    // Coordinate: interval minute = 1 titik per baris jadi "Coordinate" tunggal cukup;
    // interval lebih besar (hour/day/dst) tiap baris ngerangkum banyak titik, jadi dipisah First/Last
    // biar gak kehilangan posisi akhir bucket.
    gps: config.interval === 'minute' ? [
      {
        key: 'coordinate', label: 'Coordinate', minWidth: 200, visible: true,
      },
      {
        key: 'heading', label: 'Heading (°)', minWidth: 100, visible: true,
      },
      // Speed & Cruise: label diberi satuan dinamis di computed visibleColumns (ikut dropdown unit)
      {
        key: 'speed', label: 'Speed', minWidth: 100, visible: true,
      },
      {
        key: 'cruise', label: 'Cruise', minWidth: 100, visible: true,
      },
    ] : [
      {
        key: 'firstCoordinate', label: 'First Coordinate', minWidth: 200, visible: true,
      },
      {
        key: 'lastCoordinate', label: 'Last Coordinate', minWidth: 200, visible: true,
      },
      {
        key: 'heading', label: 'Heading (°)', minWidth: 100, visible: true,
      },
      {
        key: 'speed', label: 'Speed', minWidth: 100, visible: true,
      },
      {
        key: 'cruise', label: 'Cruise', minWidth: 100, visible: true,
      },
    ],

    // Data gelombang & marine jadi kategori sendiri biar gampang difilter.
    // Wave Height & Period pakai rata-rata agregat per interval (AVG_dlgp*);
    // arah & kondisi pakai nilai representatif dari gpsData (bisa null utk device non-gelombang).
    wave: [
      {
        key: 'waveHeight', label: 'Wave Height (m)', minWidth: 120, visible: true,
      },
      {
        key: 'waveDir', label: 'Wave Dir (°)', minWidth: 110, visible: true,
      },
      {
        key: 'wavePeriod', label: 'Wave Period (s)', minWidth: 120, visible: true,
      },
      {
        key: 'relDir', label: 'Relative Dir (°)', minWidth: 120, visible: true,
      },
      {
        key: 'wisScore', label: 'WIS Score', minWidth: 100, visible: true,
      },
      {
        key: 'seaState', label: 'Sea State', minWidth: 110, visible: true,
      },
    ],
  };

  // RPM per engine (RPM + Running Hours). Flow & temp dipisah ke kategori sendiri.
  rpmTypes.forEach((rpmType) => {
    const key = rpmType.toLowerCase();
    // Helper label (CENTER -> MAIN kalau cuma 1 tipe RPM).
    const displayLabel = config.rpmTypeLabel(rpmType, rpmTypes);
    columns[key] = [
      {
        key: `${key}Rpm`, label: `${displayLabel} RPM (rpm)`, minWidth: 100, visible: true,
      },
      {
        key: `${key}RunningHours`, label: `${displayLabel} Run. Hours`, minWidth: 100, visible: true,
      },
    ];
  });

  // Flowmeter per group (label-driven, tiap line jadi kategori sendiri: fm_<name>).
  config.flowmeterGroups.forEach((group) => {
    const { name } = group;
    const key = name.toLowerCase();
    const lbl = config.flowmeterLabel(name);
    const cols = [];
    if (group.hasOut) {
      cols.push({
        key: `${key}InVolumeFlowrate`, label: `${lbl} In Flowrate (L/h)`, minWidth: 120, visible: true,
      });
      cols.push({
        key: `${key}InTemperature`, label: `${lbl} In Temp. (°C)`, minWidth: 120, visible: true,
      });
      cols.push({
        key: `${key}InDensity`, label: `${lbl} In Density (g/cm³)`, minWidth: 130, visible: true,
      });
      cols.push({
        key: `${key}OutVolumeFlowrate`, label: `${lbl} Out Flowrate (L/h)`, minWidth: 120, visible: true,
      });
      cols.push({
        key: `${key}OutTemperature`, label: `${lbl} Out Temp. (°C)`, minWidth: 120, visible: true,
      });
      cols.push({
        key: `${key}OutDensity`, label: `${lbl} Out Density (g/cm³)`, minWidth: 130, visible: true,
      });
    } else {
      cols.push({
        key: `${key}VolumeFlowrate`, label: `${lbl} Flowrate (L/h)`, minWidth: 110, visible: true,
      });
      cols.push({
        key: `${key}VolumeInventory`, label: `${lbl} Vol. Inv. (L)`, minWidth: 110, visible: true,
      });
      cols.push({
        key: `${key}Temperature`, label: `${lbl} Temp. (°C)`, minWidth: 110, visible: true,
      });
      cols.push({
        key: `${key}Density`, label: `${lbl} Density (g/cm³)`, minWidth: 120, visible: true,
      });
    }
    columns[`fm_${key}`] = cols;
  });

  // Exhaust temperature per posisi (data-driven, lepas dari RPM).
  if (config.exhaustPositions.length) {
    columns.exhaust = config.exhaustPositions.map((pos) => ({
      key: `temperatureExhaust${pos}`,
      label: `${config.rpmTypeLabel(pos, rpmTypes)} Exhaust Temp. (°C)`,
      minWidth: 120,
      visible: true,
    }));
  }

  // Reservoir tank temperature per posisi (data-driven).
  if (config.tankPositions.length) {
    columns.reservoir = config.tankPositions.map((pos) => ({
      key: `temperatureTank${pos}`,
      label: `${config.rpmTypeLabel(pos, rpmTypes)} Reservoir Temp. (°C)`,
      minWidth: 120,
      visible: true,
    }));
  }

  if (aeCount > 0) {
    columns.ae = [];
    for (let i = 1; i <= aeCount; i += 1) {
      columns.ae.push({
        key: `ae${i}RunningHours`, label: `AE${i} Run. Hours`, minWidth: 100, visible: true,
      });
      // AE Fuel Consumption sengaja gak ada di jalur series: tabel `ae` cuma
      // punya kolom dlaeRpm (running time detik), angka BBM-nya dihitung
      // endpoint summary dari tarif per aset.
    }
  }

  if (config.powermeterDevices.length) {
    columns.powermeter = [];
    config.powermeterDevices.forEach((pmDeviceKey) => {
      const pmName = pmDeviceKey.replace('PM_', '').replace('_', ' ');
      const pmKey = pmDeviceKey.toLowerCase();
      columns.powermeter.push({
        key: `${pmKey}ActivePower`, label: `${pmName} Active Power (kW)`, minWidth: 120, visible: true,
      });
      columns.powermeter.push({
        key: `${pmKey}ApparentPower`, label: `${pmName} Apparent Power (kVA)`, minWidth: 120, visible: true,
      });
      columns.powermeter.push({
        key: `${pmKey}TotalEnergy`, label: `${pmName} Total Energy (kWh)`, minWidth: 120, visible: true,
      });
      columns.powermeter.push({
        key: `${pmKey}VoltageLtoL`, label: `${pmName} Voltage L-L (V)`, minWidth: 120, visible: true,
      });
      columns.powermeter.push({
        key: `${pmKey}VoltageLtoN`, label: `${pmName} Voltage L-N (V)`, minWidth: 120, visible: true,
      });
      columns.powermeter.push({
        key: `${pmKey}Current`, label: `${pmName} Current (A)`, minWidth: 120, visible: true,
      });
      columns.powermeter.push({
        key: `${pmKey}Frequency`, label: `${pmName} Frequency (Hz)`, minWidth: 120, visible: true,
      });
      columns.powermeter.push({
        key: `${pmKey}PowerFactor`, label: `${pmName} Power Factor`, minWidth: 120, visible: true,
      });
      columns.powermeter.push({
        key: `${pmKey}RunningSeconds`, label: `${pmName} Run. Hours`, minWidth: 120, visible: true,
      });
    });
  }

  // Grup tetap ikut tiap kolom: header udah datar, tapi modal Filter Column
  // masih ngelompokin checkbox pakai ini.
  return Object.entries(columns).flatMap(
    ([group, list]) => list.map((col) => ({ ...col, group })),
  );
}

function speedText(series, unit) {
  if (typeof series?.gpsData?.speed === 'undefined') return '-';
  if (unit.speed === 'knot') return formatNum(kmhToKnot(series.gpsData.speed), 2);
  return formatNum(series.gpsData.speed, 2);
}

function cruiseText(series, unit) {
  if (typeof series?.gpsData?.additional?.SUM_dlgpDistance_3 === 'undefined') return '-';
  const distance = series.gpsData.additional.SUM_dlgpDistance_3;
  if (unit.distance === 'nm') return formatNum(metersToNm(distance), 2);
  return formatNum((distance * 0.001), 2);
}

// Bangun array row dari list series. Salinan buildRows FmsReportDatalog.vue,
// isSuperAdmin gak ikut pindah — halaman yang gabungin sebelum kirim highlightAnomaly.
export function buildDatalogRows(seriesList, config) {
  const data = [];
  const { aeCount = 0 } = config;

  if (!seriesList || !seriesList.length) {
    return data;
  }

  const rpmTypes = config.rpmTypes;
  const fmGroups = config.flowmeterGroups;
  const { exhaustPositions, tankPositions } = config;
  const highlightOn = config.highlightAnomaly;

  seriesList.forEach((series) => {
    const row = {
      timestamp: series.timestamp,
      dateTime: formatDateTime(series.timestamp * 1000, DISPLAY_DATETIME),
      coordinate: typeof series?.gpsData?.latitude !== 'undefined' && typeof series?.gpsData?.longitude !== 'undefined'
        ? `${convertDecimalToDMS(series?.gpsData?.latitude, true)}, ${convertDecimalToDMS(series?.gpsData?.longitude, false)}`
        : '-',
      firstCoordinate: typeof series?.gpsData?.latitude !== 'undefined' && typeof series?.gpsData?.longitude !== 'undefined'
        ? `${convertDecimalToDMS(series?.gpsData?.latitude, true)}, ${convertDecimalToDMS(series?.gpsData?.longitude, false)}`
        : '-',
      lastCoordinate: typeof series?.gpsData?.additional?.LAST_dlgpLatitude_4 !== 'undefined' && typeof series?.gpsData?.additional?.LAST_dlgpLongitude_5 !== 'undefined'
        ? `${convertDecimalToDMS(series.gpsData.additional.LAST_dlgpLatitude_4, true)}, ${convertDecimalToDMS(series.gpsData.additional.LAST_dlgpLongitude_5, false)}`
        : '-',
      speed: speedText(series, config.unit),
      heading: typeof series?.gpsData?.track !== 'undefined' ? formatNum(series?.gpsData?.track, 2) : '-',
      cruise: cruiseText(series, config.unit),
      // Wave Height & Period: rata-rata agregat per interval (samakan pola dgn cruise yg pakai additional)
      waveHeight: typeof series?.gpsData?.additional?.AVG_dlgpWaveHeight_9 === 'number'
        ? formatNum(series.gpsData.additional.AVG_dlgpWaveHeight_9, 2)
        : '-',
      wavePeriod: typeof series?.gpsData?.additional?.AVG_dlgpWavePeriod_13 === 'number'
        ? formatNum(series.gpsData.additional.AVG_dlgpWavePeriod_13, 2)
        : '-',
      // Arah/kondisi gelombang: nilai representatif, bisa null utk device tanpa data gelombang
      waveDir: typeof series?.gpsData?.waveDir === 'number'
        ? formatNum(series.gpsData.waveDir, 2)
        : '-',
      relDir: typeof series?.gpsData?.relDir === 'number'
        ? formatNum(series.gpsData.relDir, 2)
        : '-',
      wisScore: typeof series?.gpsData?.wisScore === 'number'
        ? series.gpsData.wisScore
        : '-',
      seaState: series?.gpsData?.seaState
        ? String(series.gpsData.seaState).toUpperCase()
        : '-',

    };

    // RPM per engine (RPM + Running Hours). Nilai RPM disimpan utk cek anomali.
    const rpmByType = {};
    rpmTypes.forEach((rpmType) => {
      const key = rpmType.toLowerCase();
      const rpmTypeUpper = rpmType.toUpperCase();
      const rpmData = series?.rpmData?.[rpmTypeUpper] || null;
      rpmByType[rpmTypeUpper] = typeof rpmData?.rpm === 'number' ? rpmData.rpm : null;
      row[`${key}Rpm`] = typeof rpmData?.rpm !== 'undefined' ? formatNum(rpmData.rpm, 2) : '-';
      row[`${key}RunningHours`] = typeof rpmData?.additional?.COUNTGT_dlrpRpm_1 !== 'undefined'
        ? toDuration(rpmData.additional.COUNTGT_dlrpRpm_1 * 60)
        : '-';
    });

    // Flowmeter per group (label-driven, lepas dari RPM). Net flow disimpan per nama group.
    const netFlowByName = {};
    fmGroups.forEach((group) => {
      const { name } = group;
      const key = name.toLowerCase();
      const inKey = `${name}_IN`;
      const fmDataIn = series?.fmData?.[inKey] || null;

      if (group.hasOut) {
        const outKey = `${name}_OUT`;
        const fmDataOut = series?.fmData?.[outKey] || null;
        row[`${key}InVolumeFlowrate`] = typeof fmDataIn?.volumeFlowrate !== 'undefined' ? formatNum(fmDataIn.volumeFlowrate, 2) : '-';
        row[`${key}InTemperature`] = typeof fmDataIn?.temperature !== 'undefined' ? formatNum(fmDataIn.temperature, 2) : '-';
        row[`${key}InDensity`] = typeof fmDataIn?.density !== 'undefined' ? formatNum(fmDataIn.density, 2) : '-';
        row[`${key}OutVolumeFlowrate`] = typeof fmDataOut?.volumeFlowrate !== 'undefined' ? formatNum(fmDataOut.volumeFlowrate, 2) : '-';
        row[`${key}OutTemperature`] = typeof fmDataOut?.temperature !== 'undefined' ? formatNum(fmDataOut.temperature, 2) : '-';
        row[`${key}OutDensity`] = typeof fmDataOut?.density !== 'undefined' ? formatNum(fmDataOut.density, 2) : '-';

        // FLOW = Net (IN - OUT); OUT yang kosong dianggap 0
        const inFlow = typeof fmDataIn?.volumeFlowrate === 'number' ? fmDataIn.volumeFlowrate : null;
        const outFlow = typeof fmDataOut?.volumeFlowrate === 'number' ? fmDataOut.volumeFlowrate : 0;
        netFlowByName[name.toUpperCase()] = inFlow === null ? null : inFlow - outFlow;
      } else {
        row[`${key}VolumeFlowrate`] = typeof fmDataIn?.volumeFlowrate !== 'undefined' ? formatNum(fmDataIn.volumeFlowrate, 2) : '-';
        row[`${key}VolumeInventory`] = typeof fmDataIn?.additional?.FIRST_dlfmVolInventory_0 !== 'undefined'
          ? formatNum(fmDataIn.additional.FIRST_dlfmVolInventory_0, 2)
          : '-';
        row[`${key}Temperature`] = typeof fmDataIn?.temperature !== 'undefined' ? formatNum(fmDataIn.temperature, 2) : '-';
        row[`${key}Density`] = typeof fmDataIn?.density !== 'undefined' ? formatNum(fmDataIn.density, 2) : '-';

        netFlowByName[name.toUpperCase()] = typeof fmDataIn?.volumeFlowrate === 'number' ? fmDataIn.volumeFlowrate : null;
      }
    });

    // Exhaust & reservoir temp per posisi (data-driven, lepas dari RPM).
    exhaustPositions.forEach((pos) => {
      const grp = series?.temperatureData?.EXHAUST_TEMPERATURE?.[pos];
      const t = grp && grp[0] ? grp[0] : null;
      row[`temperatureExhaust${pos}`] = typeof t?.additional?.AVG_dltmTemp_2 !== 'undefined'
        ? formatNum(t.additional.AVG_dltmTemp_2, 2)
        : '-';
    });
    tankPositions.forEach((pos) => {
      const grp = series?.temperatureData?.TEMPERATURE?.[pos];
      const t = grp && grp[0] ? grp[0] : null;
      row[`temperatureTank${pos}`] = typeof t?.additional?.AVG_dltmTemp_2 !== 'undefined'
        ? formatNum(t.additional.AVG_dltmTemp_2, 2)
        : '-';
    });

    // Anomali RPM vs FLOW: cuma utk engine yang punya pasangan (RPM & flowmeter nama sama).
    let hasRpmNoFlow = false; // RPM > 0 tapi FLOW = 0
    let hasFlowNoRpm = false; // RPM = 0 tapi FLOW > 0
    rpmTypes.forEach((rpmType) => {
      const up = rpmType.toUpperCase();
      const rpmValue = rpmByType[up];
      const flowValue = netFlowByName[up];
      if (rpmValue !== null && rpmValue !== undefined && flowValue !== null && flowValue !== undefined) {
        const rpmRounded = Math.round(rpmValue * 100) / 100;
        const flowRounded = Math.round(flowValue * 100) / 100;
        if (rpmRounded > 0 && flowRounded === 0) hasRpmNoFlow = true;
        if (rpmRounded === 0 && flowRounded > 0) hasFlowNoRpm = true;
      }
    });
    // Tandai baris (super admin + toggle aktif). RPM>0 & FLOW 0 lebih kritis -> diprioritaskan.
    if (highlightOn && hasRpmNoFlow) {
      row.rowClass = 'anomaly-rpm-no-flow';
    } else if (highlightOn && hasFlowNoRpm) {
      row.rowClass = 'anomaly-flow-no-rpm';
    } else {
      row.rowClass = '';
    }

    for (let i = 1; i <= aeCount; i += 1) {
      const aeKey = `AE${i}`;
      const aeData = series && series.aeData ? series.aeData[aeKey] : null;
      row[`ae${i}RunningHours`] = typeof aeData?.runningSeconds !== 'undefined'
        ? toDuration(aeData.runningSeconds)
        : '-';
    }

    if (series.powermeterData) {
      Object.keys(series.powermeterData).forEach((pmDeviceKey) => {
        const pmData = series.powermeterData[pmDeviceKey];
        const pmKey = pmDeviceKey.toLowerCase();

        row[`${pmKey}ActivePower`] = typeof pmData?.activePower !== 'undefined' ? formatNum(pmData.activePower, 2) : '-';
        row[`${pmKey}ApparentPower`] = typeof pmData?.apparentPower !== 'undefined' ? formatNum(pmData.apparentPower, 2) : '-';
        row[`${pmKey}TotalEnergy`] = typeof pmData?.totalEnergy !== 'undefined' ? formatNum(pmData.totalEnergy, 2) : '-';
        row[`${pmKey}VoltageLtoL`] = typeof pmData?.voltageLtoL !== 'undefined' ? formatNum(pmData.voltageLtoL, 2) : '-';
        row[`${pmKey}VoltageLtoN`] = typeof pmData?.voltageLtoN !== 'undefined' ? formatNum(pmData.voltageLtoN, 2) : '-';
        row[`${pmKey}Current`] = typeof pmData?.current !== 'undefined' ? formatNum(pmData.current, 2) : '-';
        row[`${pmKey}Frequency`] = typeof pmData?.frequency !== 'undefined' ? formatNum(pmData.frequency, 2) : '-';
        row[`${pmKey}PowerFactor`] = typeof pmData?.powerFactor !== 'undefined' ? formatNum(pmData.powerFactor, 2) : '-';
        row[`${pmKey}RunningSeconds`] = typeof pmData?.runningSeconds !== 'undefined' ? toDuration(pmData.runningSeconds) : '-';
      });
    }

    data.push(row);
  });

  return data;
}
